import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link, navigate, safeNext, useLocation } from '../../app/router';
import { ApiError } from '../../lib/api';
import { Button, Input, linkClass } from '../../ui';
import { AuthLayout, FormError } from './AuthLayout';
import { callbackUrl, emailSchema } from './schemas';
import { useCaptcha } from './Turnstile';

export function LoginPage() {
  const { t } = useTranslation();
  const api = useApi();
  const { search } = useLocation();
  const next = safeNext(new URLSearchParams(search).get('next'));
  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const captcha = useCaptcha();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) return setError(t('auth.invalidEmail'));
    setBusy(true);
    try {
      if (mode === 'password') {
        await api.auth.signInWithPassword(parsed.data, password, captcha.token);
        navigate(next, { replace: true });
      } else {
        await api.auth.signInWithOtp(parsed.data, callbackUrl(next), captcha.token);
        setSent(true);
      }
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      // A magic link has one answer whatever the address (P17): once sign-ups are closed, GoTrue refuses an unknown
      // address, and showing that would tell whether an account exists. Only "could not ask at all" is said.
      if (mode === 'magic' && code !== 'network') setSent(true);
      else setError(code === 'unauthorized' ? t('auth.invalidCredentials') : t('auth.genericError'));
      captcha.reset(); // a Turnstile token is single-use
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title={t('auth.checkEmail')}>
        <p className="font-sans text-fg-muted">{t('auth.checkEmailBody', { email })}</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.signInTitle')}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('auth.email')}
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        {mode === 'password' && (
          <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
            {t('auth.password')}
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        )}
        {captcha.widget}
        <FormError message={error} />
        <Button variant="primary" type="submit" disabled={busy || !captcha.ready}>
          {mode === 'password' ? t('auth.signIn') : t('auth.sendMagicLink')}
        </Button>
      </form>
      <div className="flex flex-col gap-3">
        <Button onClick={() => setMode(mode === 'password' ? 'magic' : 'password')}>
          {mode === 'password' ? t('auth.useMagicLink') : t('auth.usePassword')}
        </Button>
        <Button onClick={() => api.auth.signInWithGoogle(callbackUrl(next)).catch(() => setError(t('auth.genericError')))}>
          {t('auth.google')}
        </Button>
      </div>
      <p className="flex flex-wrap gap-4">
        <Link className={linkClass} to={href('reset')}>
          {t('auth.forgot')}
        </Link>
      </p>
      {/* P17: no self sign-up; people come in through an invitation link. */}
      <p className="font-sans text-sm text-fg-muted">{t('auth.inviteOnly')}</p>
    </AuthLayout>
  );
}
